import {
  SpaceBetween,
  Toggle,
  TopNavigation,
  TopNavigationProps,
  RadioGroup,
} from "@cloudscape-design/components";
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Logo from "../../../assets/images/Logo";
import { useAuthContext } from "../../../auth/useAuthContext";
import TitleSelect from "../../../components/TitleSelection/TitleSelect";
import { SERVICES, TYPES } from "../../../config-global";
import { useGetGenericPreference_V2 } from "../../../services/generic_v2/hooks/useGetGenericPreference_V2";
import { usePostGenericPreference_V2 } from "../../../services/generic_v2/hooks/usePostGenericPreference_V2";
import { usePutGenericPreference_V2 } from "../../../services/generic_v2/hooks/usePutGenericPreference_V2";
import { invalidatePreferenceData } from "../../../services/generic_v2/utils/serviceUtils";
import { FavoritesBarList } from "./FavoriresBarList/FavoritesBarList";
import { ModeToggle } from "./ModeToggle";
import { FavoriteServices, ServicesMenu } from "./services-menu/ServicesMenu";
import { Vertical } from "../../../types/verticalTypes";


export default function Header({ title }: { title: string }) {
  const apiParams = { type: TYPES.PERSONAL, service: SERVICES.FAVORITES };
  const { logout, user } = useAuthContext();
  const [favoriteServices, setFavoriteServices] = useState<FavoriteServices>({});

  const { data: favoritesPreferenceData } = useGetGenericPreference_V2({
    apiParams,
  });
  const { mutate: updatePreference } = usePutGenericPreference_V2();
  const { mutate: postGenericPreference } = usePostGenericPreference_V2({
    apiParams,
    onSuccess: invalidatePreferenceData,
  });

  const isAdmin = user?.role === "superAdmin" || user?.role === "admin";
  const isMobile = window.screen.width < 768;

  useEffect(() => {
    const pref = favoritesPreferenceData?.[0]?.data;

    if (pref) {
      const newPref = { ...pref };
      const itemsToDelete = [
        "/platforms/google"
      ];
      itemsToDelete.forEach((item) => {
        if (newPref[item]) delete newPref[item];
      });
      setFavoriteServices(newPref);
    }
  }, [favoritesPreferenceData]);

  const updateFavorites = useCallback(
    (data: FavoriteServices) => {
      updatePreference({ id: favoritesPreferenceData?.[0]?.id, payload: data });
    },
    [favoritesPreferenceData, updatePreference],
  );

  const handleFavorites = (path: string, label: string) => {
    const prefValue = favoritesPreferenceData?.[0];

    if (Object.keys(prefValue?.data ?? {}).length > 0) {
      const { data } = prefValue;

      if (data[path]) delete data[path];
      else data[path] = label;

      updateFavorites(data);
      return;
    }

    const data = { [path]: label };
    postGenericPreference(data);
  };

  return (
    <div className="flex flex-col border-b border-border bg-appBg awsui-context-top-navigation">
      <div className="grid grid-cols-[auto_1fr_auto]">
        <div className={`flex items-center ${isMobile ? "ml-4" : "gap-5 ml-6"}`}>
          {!isMobile && (
            <>
              <Link to="/">
                <Logo width={8} height={8} />
              </Link>
              <div className="border-l border-border h-8" />
            </>
          )}
          <ServicesMenu
            favoriteServices={favoriteServices}
            handleFavorites={handleFavorites}
          />
        </div>
        <TopNavigation
          identity={{} as TopNavigationProps.Identity}
          search={
            <TitleSelect 
              verticals={["all", Vertical.Movies, Vertical.Series, Vertical.Games, Vertical.Franchises, Vertical.Talent, Vertical.Books, Vertical.Anime, Vertical.Manga]}
              onSelect={(selectedOption) => {
                if (selectedOption.value.startsWith("nm")) {
                  window.location = `${window.location.origin}/item/talent/${selectedOption.value}` as any;
                } else {
                  window.location = `${window.location.origin}/item/${selectedOption.value}` as any;
                }
              }}
              localStorageKey="searchCategory"
              limit={25}
            />
          }
          i18nStrings={{
            searchIconAriaLabel: "Search",
            searchDismissIconAriaLabel: "Close search",
            overflowMenuTriggerText: "More",
            overflowMenuTitleText: "All",
            overflowMenuBackIconAriaLabel: "Back",
            overflowMenuDismissIconAriaLabel: "Close menu",
          }}
          utilities={[
            isAdmin ? {
              type: "menu-dropdown",
              iconName: "settings",
              ariaLabel: "Settings",
              title: "Settings",
              items: [
                {
                  text: "Control Panel",
                  items: [
                    {
                      id: "data",
                      text: "Triggers",
                      href: "/control-panel/triggers",
                    },
                    {
                      id: "data",
                      text: "Tag Editor",
                      href: "/control-panel/tag-editor",
                    },
                    {
                      id: "data",
                      text: "Audit Logs",
                      href: "/control-panel/audit-logs",
                    },
                  ],
                },
              ],
            } : null,
            {
              type: "menu-dropdown",
              text: user.name,
              description: user.email,
              iconName: "user-profile",
              onItemClick: (e) => {
                if (e.detail.id === "signout") {
                  logout();
                }
              },
              items: [
                { id: "profile", text: "Profile" },
                { id: "signout", text: "Sign out" },
              ],
            },
          ].filter(Boolean) as TopNavigationProps.Utility[] }
        />
        <ModeToggle />
        <style>{`
          [class^="awsui_top-navigation_"] {
            border-block-end: none !important;
          }
        `}
        </style>
      </div>
      {!isMobile && (
        <FavoritesBarList
          favoriteServices={favoriteServices}
          setFavoriteServices={setFavoriteServices}
          updateFavorites={updateFavorites}
        />
      )}
    </div>
  );
}
